import React, { useEffect, useState } from "react";
import registroSensoresService from "../../services/registroSensoresService";

// helpers export/print (comparten estilo con ListaSensores)
const escapeCsv = (val) => {
  const s = String(val ?? "");
  const needsQuotes = /[",\r\n;]/.test(s);
  const escaped = s.replace(/"/g, '""');
  return needsQuotes ? `"${escaped}"` : escaped;
};

const TablaRegistrosSensor = ({
  sensor,        // { id, nombre, unidad, tipoParametroNombre, dispositivoNombre, estanqueNombre }
  defaultPageSize = 15,
}) => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [total, setTotal] = useState(0);
  const [desde, setDesde] = useState("");     // ISO local
  const [hasta, setHasta] = useState("");
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");

  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));

  const fetchData = async () => {
    setCargando(true);
    setError("");
    try {
      const resp = await registroSensoresService.getPorSensor(sensor.id, {
        page,
        pageSize,
        desdeUtc: desde ? new Date(desde).toISOString() : null,
        hastaUtc: hasta ? new Date(hasta).toISOString() : null,
        desc: true,
      });
      setItems(resp.items || []);
      setTotal(resp.total || 0);
    } catch (e) {
      console.error(e);
      setError("No se pudieron cargar los registros.");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, desde, hasta, sensor.id]);

  // Export CSV por tabla
  const exportCsv = () => {
    const headers = ["Fecha (UTC)", "Valor", "Unidad", "Tipo", "Sensor", "Dispositivo", "Estanque"];
    const rows = items.map(r => ([
      new Date(r.fechaRegistroUtc).toISOString(),
      String(r.valor),
      r.unidad ?? "",
      r.tipoParametroNombre ?? "",
      r.sensorNombre ?? "",
      r.dispositivoNombre ?? "",
      r.estanqueNombre ?? "",
    ]));
    const csv = [headers.map(escapeCsv).join(","), ...rows.map(row => row.map(escapeCsv).join(","))].join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const iso = new Date().toISOString().slice(0,19).replace(/[:T]/g,"-");
    a.download = `registros-${sensor.nombre}-${iso}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Imprimir (tabla local)
  const printTable = () => {
    const title = `Registros • ${sensor.estanqueNombre} | ${sensor.dispositivoNombre} | ${sensor.nombre}`;
    const headers = "<tr><th>Fecha (UTC)</th><th>Valor</th><th>Unidad</th><th>Tipo</th><th>Sensor</th><th>Dispositivo</th><th>Estanque</th></tr>";
    const rowsHtml = items.map(r => `
      <tr>
        <td>${new Date(r.fechaRegistroUtc).toISOString()}</td>
        <td style="text-align:right">${r.valor}</td>
        <td>${r.unidad ?? ""}</td>
        <td>${r.tipoParametroNombre ?? ""}</td>
        <td>${r.sensorNombre ?? ""}</td>
        <td>${r.dispositivoNombre ?? ""}</td>
        <td>${r.estanqueNombre ?? ""}</td>
      </tr>`).join("");

    const html = `<!doctype html><html><head><meta charset="utf-8"/>
    <title>${title}</title>
    <style>
      body{font-family:system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;color:#111}
      h1{font-size:18px;margin:0 0 8px}
      table{border-collapse:collapse;width:100%;font-size:12px}
      th,td{border:1px solid #0ea5a3;padding:6px}
      thead th{background:#111827;color:#67e8f9}
      tr:nth-child(even) td{background:#f8fafc}
    </style></head><body>
    <h1>${title}</h1>
    <div style="color:#475569;font-size:12px;margin:6px 0">Mostrando ${items.length} de ${total}</div>
    <table><thead>${headers}</thead><tbody>${rowsHtml}</tbody></table>
    <script>window.onload=()=>{window.print();setTimeout(()=>window.close(),300);};</script>
    </body></html>`;

    const w = window.open("", "_blank");
    if (!w) return;
    w.document.open(); w.document.write(html); w.document.close();
  };

  const disabledPrev = page <= 1;
  const disabledNext = page >= totalPages;

  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900 p-3 mt-3">
      {/* Encabezado */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="text-sm">
          <div className="font-semibold text-cyan-300">{sensor.nombre} <span className="text-slate-400">({sensor.tipoParametroNombre}{sensor.unidad ? ` · ${sensor.unidad}` : ""})</span></div>
          <div className="text-slate-400">{sensor.dispositivoNombre} — {sensor.estanqueNombre}</div>
        </div>
        <div className="flex items-center gap-2">
          {/* rango corto */}
          <input type="datetime-local" value={desde} onChange={e=>{setPage(1);setDesde(e.target.value)}} className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs"/>
          <span className="text-slate-400 text-xs">→</span>
          <input type="datetime-local" value={hasta} onChange={e=>{setPage(1);setHasta(e.target.value)}} className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs"/>
          <button onClick={()=>{setDesde("");setHasta("");}} className="text-xs bg-slate-800 border border-slate-700 rounded px-2 py-1">Limpiar</button>
          <button onClick={printTable} className="text-xs bg-cyan-700 hover:bg-cyan-600 text-white rounded px-2 py-1">Imprimir</button>
          <button onClick={exportCsv} className="text-xs bg-emerald-700 hover:bg-emerald-600 text-white rounded px-2 py-1">Exportar</button>
        </div>
      </div>

      {/* Tabla */}
      <div className="mt-2 overflow-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-800 text-cyan-200">
              <th className="px-2 py-2 text-left">Fecha (UTC)</th>
              <th className="px-2 py-2 text-right">Valor</th>
              <th className="px-2 py-2 text-left">Unidad</th>
              <th className="px-2 py-2 text-left">Tipo</th>
              <th className="px-2 py-2 text-left">Sensor</th>
              <th className="px-2 py-2 text-left">Dispositivo</th>
              <th className="px-2 py-2 text-left">Estanque</th>
            </tr>
          </thead>
          <tbody>
            {cargando && <tr><td colSpan={7} className="px-3 py-3 text-center text-slate-300">Cargando…</td></tr>}
            {!cargando && error && <tr><td colSpan={7} className="px-3 py-3 text-center text-red-400">{error}</td></tr>}
            {!cargando && !error && items.length === 0 && <tr><td colSpan={7} className="px-3 py-3 text-center text-slate-400">Sin datos.</td></tr>}
            {!cargando && !error && items.map(r => (
              <tr key={r.id} className="odd:bg-slate-900 even:bg-slate-950">
                <td className="px-2 py-1">{new Date(r.fechaRegistroUtc).toISOString()}</td>
                <td className="px-2 py-1 text-right">{r.valor}</td>
                <td className="px-2 py-1">{r.unidad ?? ""}</td>
                <td className="px-2 py-1">{r.tipoParametroNombre}</td>
                <td className="px-2 py-1">{r.sensorNombre}</td>
                <td className="px-2 py-1">{r.dispositivoNombre}</td>
                <td className="px-2 py-1">{r.estanqueNombre}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer: paginación y pageSize */}
      <div className="mt-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs">
          <span>Página {page} de {totalPages}</span>
          <button disabled={disabledPrev} onClick={()=>setPage(p=>Math.max(1, p-1))}
            className={`px-2 py-1 rounded border ${disabledPrev ? "opacity-40 cursor-not-allowed" : "hover:bg-slate-800"}`}>←</button>
          <button disabled={disabledNext} onClick={()=>setPage(p=>p+1)}
            className={`px-2 py-1 rounded border ${disabledNext ? "opacity-40 cursor-not-allowed" : "hover:bg-slate-800"}`}>→</button>
        </div>
        <div className="text-xs flex items-center gap-2">
          <span>Registros por página:</span>
          <select value={pageSize} onChange={e=>{setPage(1);setPageSize(parseInt(e.target.value,10)||15)}} className="bg-slate-900 border border-slate-700 rounded px-2 py-1">
            {[5,10,15,20,30,50,100,200].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>
    </div>
  );
};

export default TablaRegistrosSensor;
